import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Users, ChevronDown, ChevronRight, LogOut, Search,
  Bot, Zap, Inbox, MessageCircle, Phone, Mail, Palette, FileText,
  History, Sparkles, BarChart3, LineChart, Calendar, FlaskConical,
  Globe, UsersRound, Crown, Shield, UserCheck, Layers, Plus, X,
  Menu, Bell, Target, Settings, User, CreditCard
} from 'lucide-react';
import { Button } from './ui/button';
import ChatWidget from './ChatWidget';
import { toast } from 'sonner';
import { useWebSocket, requestNotificationPermission } from '../hooks/useWebSocket';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROBOT_MASCOT = "https://static.prod-images.emergentagent.com/jobs/1c1473f8-5054-4bfb-9a47-709ec3479a63/images/67fab65f02be6b766ed7e9a5a868d2e9a2f5db32d2c9edb603c968ef09ab7cd2.png";

// Sidebar section configuration - CLEANED UP
const SIDEBAR_SECTIONS = [
  {
    id: 'leads',
    icon: Users,
    label: 'Leads',
    badge: 'leads_count',
    items: [
      { path: '/leads', icon: Users, label: 'All Leads' },
      { path: '/lead-priority', icon: Target, label: 'AI Priority' },
      { path: '/inbox', icon: Inbox, label: 'Inbox', badge: 'unread_count' },
    ]
  },
  {
    id: 'automate',
    icon: Bot,
    label: 'Automate',
    badge: 'pending_actions',
    items: [
      { path: '/automations', icon: Zap, label: 'Automations' },
      { path: '/whatsapp', icon: MessageCircle, label: 'WhatsApp' },
      { path: '/sms', icon: Phone, label: 'SMS' },
      { path: '/voice-ai', icon: Phone, label: 'Voice AI' },
      { path: '/campaigns', icon: Mail, label: 'Campaigns' },
      { path: '/email-templates', icon: Palette, label: 'Templates' },
      { path: '/ab-testing', icon: FlaskConical, label: 'A/B Testing' },
    ]
  },
  {
    id: 'marketing',
    icon: Sparkles,
    label: 'Marketing',
    items: [
      { path: '/funnels', icon: Layers, label: 'Funnels' },
      { path: '/social', icon: Calendar, label: 'Social Media' },
      { path: '/ai-generator', icon: Sparkles, label: 'AI Content' },
      { path: '/doc-analyzer', icon: FileText, label: 'Doc Analyzer' },
    ]
  },
  {
    id: 'reports',
    icon: BarChart3,
    label: 'Reports',
    items: [
      { path: '/analytics', icon: BarChart3, label: 'Analytics' },
      { path: '/advanced-analytics', icon: LineChart, label: 'Reports' },
    ]
  },
  {
    id: 'settings',
    icon: Settings,
    label: 'Settings',
    items: [
      { path: '/profile', icon: User, label: 'Profile' },
      { path: '/domains', icon: Globe, label: 'Domains' },
      { path: '/team', icon: UsersRound, label: 'Team', roleRequired: 'team' },
      { path: '/white-label', icon: Crown, label: 'White Label' },
      { path: '/admin', icon: Shield, label: 'Admin', roleRequired: 'admin' },
      { path: '/billing', icon: CreditCard, label: 'Billing' },
    ]
  },
];

// Quick Action Button Component
const QuickActionButton = ({ onAction }) => {
  const [open, setOpen] = useState(false);
  
  const actions = [
    { id: 'lead', icon: Users, label: 'Add Lead', color: 'bg-emerald-500' },
    { id: 'whatsapp', icon: MessageCircle, label: 'Send WhatsApp', color: 'bg-green-500' },
    { id: 'call', icon: Phone, label: 'Make Call', color: 'bg-blue-500' },
    { id: 'note', icon: FileText, label: 'Add Note', color: 'bg-purple-500' },
  ];
  
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute bottom-16 right-0 space-y-2"
          >
            {actions.map((action, i) => (
              <motion.button
                key={action.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => { onAction(action.id); setOpen(false); }}
                className={`flex items-center gap-2 ${action.color} text-white px-4 py-2 rounded-full shadow-lg hover:scale-105 transition-transform`}
              >
                <action.icon className="w-4 h-4" />
                <span className="text-sm font-medium whitespace-nowrap">{action.label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      
      <button
        onClick={() => setOpen(!open)}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all ${
          open ? 'bg-red-500 rotate-45' : 'bg-emerald-500'
        }`}
        data-testid="quick-action-btn"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <Plus className="w-6 h-6 text-white" />}
      </button>
    </div>
  );
};

// Notification Bell Component
const NotificationBell = ({ count = 0 }) => {
  return (
    <button className="relative p-2 text-emerald-200 hover:text-white transition-colors" data-testid="notification-bell">
      <Bell className="w-5 h-5" />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  );
};

// Sidebar Section Component
const SidebarSection = ({ section, isOpen, onToggle, collapsed, currentPath, onNavigate, badges }) => {
  const hasActiveItem = section.items.some(item => currentPath === item.path);
  const badgeCount = badges[section.badge] || 0;
  
  if (collapsed) {
    return (
      <div className="relative group">
        <button
          onClick={onToggle}
          className={`w-full flex items-center justify-center p-3 rounded-lg transition-all ${
            hasActiveItem ? 'bg-white text-[#0a5c44]' : 'text-emerald-200 hover:bg-emerald-700/50'
          }`}
          title={section.label}
        >
          <section.icon className="w-5 h-5" />
          {badgeCount > 0 && (
            <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
              {badgeCount > 9 ? '9+' : badgeCount}
            </span>
          )}
        </button>
        
        {/* Tooltip with items */}
        <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50">
          <div className="bg-gray-900 rounded-lg shadow-xl py-2 min-w-[180px]">
            <div className="px-3 py-2 text-xs text-gray-400 font-semibold uppercase">{section.label}</div>
            {section.items.map(item => (
              <button
                key={item.path}
                onClick={() => onNavigate(item.path)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                  currentPath === item.path ? 'bg-emerald-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all ${
          hasActiveItem ? 'bg-emerald-700/70 text-white' : 'text-emerald-200 hover:bg-emerald-700/50'
        }`}
        data-testid={`section-${section.id}`}
      >
        <div className="flex items-center gap-3">
          <section.icon className="w-5 h-5" />
          <span className="font-medium">{section.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {badgeCount > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {badgeCount}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="py-1 pl-4">
              {section.items.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => onNavigate(item.path)}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                    currentPath === item.path
                      ? 'bg-white text-[#0a5c44] shadow-md'
                      : 'text-emerald-200/80 hover:bg-emerald-700/30 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="text-sm">{item.label}</span>
                  {item.badge && badges[item.badge] > 0 && (
                    <span className="ml-auto px-1.5 py-0.5 bg-emerald-500 text-white text-xs rounded">
                      {badges[item.badge]}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Layout = ({ children, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['leads']);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [badges, setBadges] = useState({
    leads_count: 0,
    unread_count: 0,
    pending_actions: 0
  });
  const [user, setUser] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize WebSocket for real-time alerts
  useWebSocket(workspaceId, {
    onConnect: () => console.log('Real-time alerts connected'),
    onDisconnect: () => console.log('Real-time alerts disconnected'),
  });
  
  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);
  
  // Get workspace ID from user data
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setWorkspaceId(parsed.workspace_id);
        setUser(parsed);
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);
  
  // Fetch badges/counts
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        
        const res = await fetch(`${API_URL}/api/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setBadges({
            leads_count: data.stats?.new_leads_today || 0,
            unread_count: 0,
            pending_actions: data.automations?.active_workflows || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch badges:', error);
      }
    };
    
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, []);
  
  // Get user info from localStorage AND API
  useEffect(() => {
    const fetchUserData = async () => {
      // First try localStorage
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {}
      }
      
      // Also fetch from API to ensure fresh data
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const res = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (data.user) {
              setUser(data.user);
              // Update localStorage with fresh data
              localStorage.setItem('user', JSON.stringify(data.user));
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    
    fetchUserData();
  }, []);
  
  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };
  
  const handleNavigate = (path) => {
    setSidebarOpen(false);
    navigate(path);
  };
  
  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'lead':
        navigate('/leads?action=add');
        break;
      case 'whatsapp':
        navigate('/whatsapp?action=compose');
        break;
      case 'call':
        navigate('/voice-ai?action=call');
        break;
      case 'note':
        toast.info('Select a lead to add a note');
        navigate('/leads');
        break;
      default:
        break;
    }
  };
  
  // Search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    const allItems = SIDEBAR_SECTIONS.flatMap(s => s.items);
    const results = allItems.filter(item => 
      item.label.toLowerCase().includes(query.toLowerCase())
    );
    setSearchResults(results);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-[#0a5c44] z-50 px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={ROBOT_MASCOT} alt="AI Lead Mantra" className="w-8 h-8" />
            <h1 className="text-lg font-heading font-bold text-white">AI Lead Mantra</h1>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell count={badges.unread_count} />
            <Button
              data-testid="mobile-menu-btn"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-emerald-700"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 h-full bg-[#0a5c44] text-white z-40 transform transition-all duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${collapsed ? 'w-20' : 'w-64'}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`p-4 border-b border-emerald-600/30 ${collapsed ? 'px-3' : 'px-4'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={ROBOT_MASCOT} alt="AI Lead Mantra" className="w-10 h-10 flex-shrink-0" />
                {!collapsed && (
                  <div>
                    <h1 className="text-lg font-heading font-bold text-white">AI Lead Mantra</h1>
                    <p className="text-xs text-emerald-300/70">Marketing Automation</p>
                  </div>
                )}
              </div>
              {!collapsed && (
                <div className="hidden lg:block">
                  <NotificationBell count={badges.leads_count} />
                </div>
              )}
            </div>
          </div>
          
          {/* Search Bar */}
          {!collapsed && (
            <div className="px-4 py-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-300/50" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-emerald-700/30 border border-emerald-600/30 rounded-lg text-white placeholder-emerald-300/50 text-sm focus:outline-none focus:border-emerald-500"
                  data-testid="sidebar-search"
                />
                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 rounded-lg shadow-xl py-2 z-50">
                    {searchResults.map(item => (
                      <button
                        key={item.path}
                        onClick={() => { handleNavigate(item.path); setSearchQuery(''); setSearchResults([]); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800"
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dashboard Link */}
          <div className={`${collapsed ? 'px-2' : 'px-4'} py-2`}>
            <Link
              to="/dashboard"
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname === '/dashboard'
                  ? 'bg-white text-[#0a5c44] shadow-lg'
                  : 'text-emerald-100 hover:bg-emerald-700/50'
              } ${collapsed ? 'justify-center px-3' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              {!collapsed && <span className="font-medium">Dashboard</span>}
            </Link>
          </div>

          {/* Navigation Sections */}
          <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-4'} overflow-y-auto py-2`}>
            {SIDEBAR_SECTIONS.map(section => (
              <SidebarSection
                key={section.id}
                section={section}
                isOpen={expandedSections.includes(section.id)}
                onToggle={() => toggleSection(section.id)}
                collapsed={collapsed}
                currentPath={location.pathname}
                onNavigate={handleNavigate}
                badges={badges}
              />
            ))}
          </nav>

          {/* User Profile & Bottom Section */}
          <div className={`border-t border-emerald-600/30 ${collapsed ? 'p-2' : 'p-4'}`} data-testid="sidebar-user-profile">
            {/* User Profile with Initials - ALWAYS show (with fallback) */}
            {!collapsed && (
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-semibold">
                  {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-white truncate">{user?.name || 'User'}</p>
                  <p className="text-[11px] text-[#94a3b8]">{user?.role || 'Admin'}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleNavigate('/profile')}
                    className="text-[#94a3b8] hover:text-white transition-colors text-base"
                    title="Settings"
                    data-testid="sidebar-settings-btn"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onLogout}
                    className="text-[#94a3b8] hover:text-red-400 transition-colors text-base"
                    title="Logout"
                    data-testid="sidebar-logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Super Admin Link - Only for super_admin users */}
            {!collapsed && user?.role === 'super_admin' && (
              <button
                onClick={() => handleNavigate('/super-admin')}
                className="w-full mt-2 p-2 bg-gradient-to-r from-violet-600/20 to-purple-600/20 hover:from-violet-600/30 hover:to-purple-600/30 border border-violet-500/30 rounded-lg text-violet-300 text-sm font-medium flex items-center justify-center gap-2 transition-all"
                data-testid="super-admin-link"
              >
                <Shield className="w-4 h-4" />
                Platform Admin
              </button>
            )}
            
            {/* Collapsed state - just icons */}
            {collapsed && (
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-semibold">
                  {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U'}
                </div>
                <button
                  onClick={onLogout}
                  className="text-red-300 hover:text-red-200 p-2"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            )}
            
            {/* Collapse Button - only show when not collapsed */}
            {!collapsed && (
              <button
                className="hidden lg:flex w-full items-center justify-center gap-2 px-4 py-2 text-emerald-200 hover:bg-emerald-700/50 rounded-lg transition-colors text-sm"
                onClick={() => setCollapsed(!collapsed)}
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
                <span>Collapse</span>
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`min-h-screen pt-16 lg:pt-0 transition-all duration-300 ${
        collapsed ? 'lg:ml-20' : 'lg:ml-64'
      }`}>
        <div className="p-4 md:p-6 lg:p-8">{children}</div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Quick Action Button */}
      <QuickActionButton onAction={handleQuickAction} />

      {/* AI Chat Widget */}
      <ChatWidget />
    </div>
  );
};

export default Layout;
